import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import ApiDataService from '../../services/ApiDataService';
import FolderTree from './FolderTree';
import Breadcrumb from './Breadcrumb';
import FileGrid from './FileGrid';
import UploadDialog from './UploadDialog';
import PreviewModal from './PreviewModal';

// File Manager has no Menu Master entry of its own — it's only ever
// embedded as a tab inside /image-optimize, so its calls correctly carry
// that host page's channel_id (there's nothing else to resolve to). The
// backend authorizes file-manager/* under the image-optimize permission
// explicitly (see BackendauthorizeFilter::menu_access_check_point).
const FileManager = () => {
  const [currentPath, setCurrentPath] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [previewEntry, setPreviewEntry] = useState(null);

  const loadFolder = useCallback((path) => {
    setLoading(true);
    ApiDataService.getAll('admin/portal/file-manager/list', `?path=${encodeURIComponent(path)}`).then(response => {
      setLoading(false);
      if (response.data.return_status === "0") {
        setEntries(response.data.result.entries || []);
      } else {
        setEntries([]);
      }
    }).catch(() => {
      setLoading(false);
      setEntries([]);
    });
  }, []);

  useEffect(() => {
    loadFolder(currentPath);
  }, [currentPath, loadFolder]);

  const handleNavigate = useCallback((path) => {
    setCurrentPath(path);
    setSearchQuery('');
    setSearchResults(null);
    setSelected({});
  }, []);

  const handleNavigateFolder = useCallback((folderName) => {
    const nextPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    handleNavigate(nextPath);
  }, [currentPath, handleNavigate]);

  useEffect(() => {
    if (!searchQuery) {
      setSearchResults(null);
      return;
    }
    const timeout = setTimeout(() => {
      ApiDataService.getAll('admin/portal/file-manager/search', `?query=${encodeURIComponent(searchQuery)}`).then(response => {
        if (response.data.return_status === "0") {
          setSearchResults(response.data.result.matches || []);
        }
      });
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const toggleSelect = (path) => {
    setSelected(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const handleBulkDelete = () => {
    const paths = Object.keys(selected).filter(p => selected[p]);
    if (paths.length === 0) return;
    if (! window.confirm(`Move ${paths.length} file(s) to trash?`)) return;
    ApiDataService.deleteWithBody('admin/portal/file-manager/delete', { paths }).then(response => {
      if (response.data.return_status === "0") {
        setSelected({});
        loadFolder(currentPath);
      }
    });
  };

  const displayedEntries = searchResults !== null
    ? searchResults.map(m => ({ ...m, is_image: /\.(jpg|jpeg|png|webp)$/i.test(m.name), thumbnail_url: null }))
    : entries;

  return (
    <Row>
      <Col md={3}>
        <FolderTree currentPath={currentPath} onNavigate={handleNavigate} />
      </Col>
      <Col md={9}>
        <Form.Control
          type="text"
          placeholder="Search files by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-2"
        />
        {searchResults === null && <Breadcrumb path={currentPath} onNavigate={handleNavigate} />}
        {loading && <div>Loading...</div>}
        <Button size="sm" className="mb-2" onClick={() => setShowUpload(true)}>Upload</Button>
        {Object.values(selected).some(Boolean) && (
          <Button size="sm" variant="danger" className="mb-2 ml-2" onClick={handleBulkDelete}>
            Delete Selected
          </Button>
        )}
        <FileGrid
          entries={displayedEntries}
          onNavigateFolder={handleNavigateFolder}
          onOpenPreview={(entry) => setPreviewEntry(entry)}
          selected={selected}
          onToggleSelect={toggleSelect}
        />
        <UploadDialog
          show={showUpload}
          currentPath={currentPath}
          onClose={() => setShowUpload(false)}
          onUploaded={() => loadFolder(currentPath)}
        />
        <PreviewModal entry={previewEntry} show={!!previewEntry} onClose={() => setPreviewEntry(null)} />
      </Col>
    </Row>
  );
};

export default FileManager;
